import { useState, useEffect } from 'react';
import { Plus, X, Server, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';

/**
 * Known server notes — community knowledge about specific Blossom servers.
 * Displayed as inline warnings/info badges next to the server URL.
 */
const SERVER_NOTES: Record<string, { type: 'warn' | 'info'; label: string; detail: string }> = {
  'blossom.primal.net': {
    type: 'warn',
    label: 'Unreliable',
    detail: 'blossom.primal.net has had multiple outages in 2025/2026. Uploaded files may be temporarily unavailable. Use as a secondary mirror, not as your only server.',
  },
  'blossom.ditto.pub': {
    type: 'info',
    label: '100 MB limit',
    detail: 'blossom.ditto.pub has a 100 MB file size limit. Large PDFs or EPUBs may fail to upload. Use another server for files over 100 MB.',
  },
  'cdn.satellite.earth': {
    type: 'warn',
    label: 'Paid',
    detail: 'cdn.satellite.earth requires a paid subscription. Uploads will fail unless you have an active account with them.',
  },
  '24242.io': {
    type: 'warn',
    label: 'Temporary',
    detail: '24242.io has a short data retention period. Files may be deleted after some time. Not suitable for permanent publications.',
  },
};

function getServerNote(url: string) {
  try {
    const hostname = new URL(url).hostname;
    return SERVER_NOTES[hostname] ?? null;
  } catch {
    return null;
  }
}

function renderServerUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname + (parsed.pathname !== '/' ? parsed.pathname.replace(/\/+$/, '') : '');
  } catch {
    return url;
  }
}

export function BlossomServerManager() {
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();

  const [servers, setServers] = useState<string[]>(config.blossomServerMetadata.servers);
  const [newServerUrl, setNewServerUrl] = useState('');

  useEffect(() => {
    setServers(config.blossomServerMetadata.servers);
  }, [config.blossomServerMetadata.servers]);

  const normalizeUrl = (url: string): string => {
    url = url.trim().replace(/\/+$/, '');
    try {
      return new URL(url).toString();
    } catch {
      try {
        return new URL(`https://${url}`).toString();
      } catch {
        return url;
      }
    }
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(normalizeUrl(url.trim()));
      return true;
    } catch {
      return false;
    }
  };

  const handleAdd = () => {
    if (!isValidUrl(newServerUrl)) {
      toast({ title: 'Invalid URL', description: 'Enter a valid Blossom server URL (e.g. https://blossom.example.com)', variant: 'destructive' });
      return;
    }
    const normalized = normalizeUrl(newServerUrl);
    if (servers.some(s => normalizeUrl(s) === normalized)) {
      toast({ title: 'Already added', description: 'This server is already in your list.', variant: 'destructive' });
      return;
    }
    const newList = [...servers, normalized];
    setServers(newList);
    setNewServerUrl('');
    saveServers(newList);
  };

  const handleRemove = (url: string) => {
    const newList = servers.filter(s => s !== url);
    setServers(newList);
    saveServers(newList);
  };

  const saveServers = (newList: string[]) => {
    const now = Math.floor(Date.now() / 1000);
    updateConfig((current) => ({
      ...current,
      blossomServerMetadata: {
        servers: newList,
        updatedAt: now,
      },
    }));

    // Publish kind 10063 (BUD-03) if logged in
    if (user) {
      publishEvent(
        {
          kind: 10063,
          content: '',
          tags: newList.map(url => ['server', url]),
        },
        {
          onSuccess: () => toast({ title: 'Server list published', description: 'Your Blossom server list has been published to Nostr.' }),
          onError: (err) => {
            console.error('Failed to publish Blossom server list:', err);
            toast({ title: 'Publish failed', description: 'Could not publish server list to Nostr.', variant: 'destructive' });
          },
        }
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Info box */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Blossom servers host your uploaded files (PDFs, EPUBs, cover images). Files are uploaded to <strong>all</strong> servers for redundancy. The first successful URL is used in the Nostr event. Add multiple servers to ensure availability.
        </AlertDescription>
      </Alert>

      {/* Merge with app defaults toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
        <div>
          <Label className="text-sm font-medium">Use app default servers</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Merge app defaults with your personal server list
          </p>
        </div>
        <Switch
          checked={config.useAppBlossomServers}
          onCheckedChange={(checked) => {
            updateConfig((current) => ({ ...current, useAppBlossomServers: checked }));
          }}
        />
      </div>

      {/* Server list */}
      <div className="space-y-2">
        {servers.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No Blossom servers configured. Add one below.</p>
        )}
        {servers.map((url) => {
          const note = getServerNote(url);
          return (
            <div key={url} className="flex items-center gap-2 p-3 rounded-md border bg-muted/20">
              <Server className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm truncate" title={url}>
                  {renderServerUrl(url)}
                </span>
                {note && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant={note.type === 'warn' ? 'destructive' : 'secondary'}
                        className="text-xs shrink-0 cursor-help"
                      >
                        {note.type === 'warn' && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {note.type === 'info' && <Info className="w-3 h-3 mr-1" />}
                        {note.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-xs">
                      {note.detail}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemove(url)}
                className="size-5 text-muted-foreground hover:text-destructive hover:bg-transparent shrink-0"
                disabled={servers.length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Add server */}
      <div className="flex gap-2">
        <Input
          placeholder="https://blossom.example.com"
          value={newServerUrl}
          onChange={(e) => setNewServerUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="flex-1"
        />
        <Button onClick={handleAdd} disabled={!newServerUrl.trim()} variant="outline" size="sm" className="h-10 shrink-0">
          <Plus className="h-4 w-4 mr-2" />Add Server
        </Button>
      </div>

      {/* Known servers reference */}
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer hover:text-foreground transition-colors font-medium py-1">
          Known Blossom servers &amp; notes
        </summary>
        <div className="mt-2 space-y-2 pl-2 border-l-2 border-muted">
          <div>
            <strong>blossom.ditto.pub</strong> — Free, reliable. 100 MB file size limit (server-side constraint that cannot be raised).
          </div>
          <div>
            <strong>blossom.dreamith.to</strong> — Free, generally reliable. Good for most files.
          </div>
          <div>
            <strong>cdn.hzrd149.com</strong> — Run by hzrd149 (Blossom creator). Reliable.
          </div>
          <div>
            <strong>blossom.primal.net</strong> — Has had repeated outages in 2025/2026. Use as a backup mirror, not as your only server.
          </div>
          <div>
            <strong>cdn.satellite.earth</strong> — Requires a paid Satellite account. Uploads will fail without one.
          </div>
          <div>
            <strong>24242.io</strong> — Short data retention. Files may be deleted after a period of inactivity. Not recommended for permanent publications.
          </div>
          <div>
            <strong>nostr.download</strong> — Free public server. Worth trying as a redundancy option.
          </div>
        </div>
      </details>

      {!user && (
        <p className="text-xs text-muted-foreground">
          Log in to sync your Blossom server list with Nostr (kind 10063).
        </p>
      )}
    </div>
  );
}
